import type {CSSProperties} from "react";
import type {Transition, Variants} from "motion/react";

/**
 * Моушен лендинга — одна таблица значений на всю страницу.
 *
 * Это НЕ новая система: числа взяты из уже принятых экранов приложения, чтобы
 * лендинг двигался так же, как всё остальное.
 *   • пружина без отскока, response 0.4 / 0.36 — `components/Main/EmptyDeck.tsx`
 *     (там же расписано, почему именно 0.4 у крупной поверхности и 0.36 у мелочи);
 *   • кривая `--rl-ease-out` = cubic-bezier(0.23, 1, 0.32, 1) — единственная
 *     кривая приложения (DESIGN_GUIDELINES §6.3);
 *   • ветка «уменьшить движение» — короткое проявление 0.18 с, как в
 *     `ConfirmDialog` (эталон по DESIGN_GUIDELINES §6.4).
 *
 * Отскока (`bounce`) здесь нет нигде намеренно. Скилл apple-design §4: перелёт
 * уместен только там, где жест принёс инерцию (бросок карточки), либо в
 * праздничный момент (сцена мэтча). Блок, который просто выехал из-под края
 * экрана, инерции не нёс — отскок читался бы как дребезг. То же правило
 * записано в DESIGN_GUIDELINES §6.4.
 */

/** Крупная поверхность: секция, панель, карточка. Apple: damping 1.0, response 0.4. */
export const LANDING_BLOCK_SPRING: Transition = {type: "spring", bounce: 0, duration: 0.4};

/** Содержимое внутри блока: строка списка, карточка в сетке, абзац. */
export const LANDING_ITEM_SPRING: Transition = {type: "spring", bounce: 0, duration: 0.36};

/** Наведение указателем — короче появления: это отклик, а не переезд. */
export const LANDING_HOVER_SPRING: Transition = {type: "spring", bounce: 0, duration: 0.28};

/** Ветка «уменьшить движение»: ни сдвигов, ни масштаба — только проявление. */
export const LANDING_FADE: Transition = {duration: 0.18, ease: "easeOut"};

/**
 * Сдвиг, с которого блок приезжает. 24 px у блока и 12 px у его содержимого:
 * за 0.4 с это меньше порога стробирования (скилл §11), но достаточно, чтобы
 * движение читалось как «блок приехал снизу», а не как мигание прозрачности.
 */
const BLOCK_OFFSET = 24;
const ITEM_OFFSET = 12;

/**
 * Разбежка детей. 0.06 с на карточку: три карточки укладываются в 0.12 с —
 * глаз видит очередь, а не ждёт последнюю. Больше 0.08 уже воспринимается как
 * «страница тормозит».
 */
export const LANDING_STAGGER_CARDS = 0.06;
export const LANDING_STAGGER_LINES = 0.045;

/**
 * Когда считать блок появившимся.
 *
 * `once: true` — прямое требование: появление ровно один раз, обратно блок не
 * прячется. `amount: 0.12` + отрицательный нижний отступ: блок трогается, когда
 * зашёл в кадр примерно на десятую часть и уже отошёл от нижней кромки, — иначе
 * анимация играет за краем экрана и человек видит только её хвост.
 *
 * У этих настроек есть слепое пятно: браузер сверяет положение блоков раз в
 * кадр, и блок, мимо которого страница пролетела за один кадр, «внутри экрана»
 * не застаётся ни разу — появление не запускается, а раз оно одноразовое, блок
 * остаётся пустым навсегда. Закрывается это не отступами, а проверкой по
 * координатам — `./useScrolledPast.ts`, там же разбор, почему отступами нельзя.
 */
export const LANDING_VIEWPORT = {once: true, amount: 0.12, margin: "0px 0px -8% 0px"} as const;

/**
 * Разбежка карточек в сетке, которая на телефоне вытянута в столбик.
 *
 * Дирижировать такой сеткой из контейнера нельзя: контейнер попадает в кадр
 * верхним краем, и вторая с третьей карточкой отыгрывают своё появление ниже
 * экрана — человек доскроллит и увидит их уже стоящими. Поэтому каждая
 * карточка следит за кадром сама, а очередь задаётся сдвигом старта по номеру.
 * На широком экране карточки лежат в ряд и входят в кадр одновременно — там эта
 * задержка и даёт ту самую очередь. Ограничение до трёх шагов: дальше ожидание
 * последней карточки становится заметным.
 */
export function cardDelay(index: number): number {
    return Math.min(index, 3) * LANDING_STAGGER_CARDS;
}

/**
 * Появление блока: сам едет и (по желанию) дирижирует детьми.
 *
 * `from` — откуда блок приезжает. По умолчанию снизу: именно оттуда его
 * выносит прокрутка, и путь появления обязан совпадать с путём, которым вещь
 * пришла (скилл §7). Сверху появляется только шапка — она и живёт сверху.
 */
export function blockVariants(
    reduceMotion: boolean,
    stagger = 0,
    delay = 0,
    from: "below" | "above" = "below",
): Variants {
    if (reduceMotion) {
        return {
            hidden: {opacity: 0},
            shown: {opacity: 1, transition: {...LANDING_FADE, delay}},
        };
    }
    return {
        hidden: {opacity: 0, y: from === "above" ? -BLOCK_OFFSET : BLOCK_OFFSET},
        shown: {
            opacity: 1,
            y: 0,
            transition: stagger
                ? {...LANDING_BLOCK_SPRING, delay, delayChildren: delay + 0.06, staggerChildren: stagger}
                : {...LANDING_BLOCK_SPRING, delay},
        },
    };
}

/**
 * Дирижёр без собственного движения: сетка карточек сама никуда не едет,
 * она только задаёт очередь детям. Двигать заодно и контейнер — значит
 * сложить два движения в одно и получить «плывущую» страницу.
 */
export function groupVariants(reduceMotion: boolean, stagger = LANDING_STAGGER_CARDS): Variants {
    return {
        hidden: {},
        shown: {
            transition: reduceMotion
                ? {staggerChildren: 0}
                : {delayChildren: 0.04, staggerChildren: stagger},
        },
    };
}

/** Появление элемента внутри блока или сетки. */
export function itemVariants(reduceMotion: boolean, delay = 0): Variants {
    if (reduceMotion) {
        return {
            hidden: {opacity: 0},
            shown: {opacity: 1, transition: {...LANDING_FADE, delay}},
        };
    }
    return {
        hidden: {opacity: 0, y: ITEM_OFFSET},
        shown: {opacity: 1, y: 0, transition: {...LANDING_ITEM_SPRING, delay}},
    };
}

/* ════════════ «ЖИВОСТЬ» СТРАНИЦЫ ══════════════════════════════════════════
 *
 * Всё ниже — одноразовые и фоновые эффекты поверх появления блоков. Правила те
 * же: только transform/opacity (кольцо совместимости — единственное исключение,
 * см. `useCountUp`), пружины без отскока, ветка «уменьшить движение» убирает
 * движение целиком.
 */

/** Подъём карточки под указателем, px. Раньше число жило прямо в `Reveal.tsx`. */
export const LANDING_HOVER_LIFT = 3;

/**
 * Параллакс первого экрана: на сколько px слой уезжает за весь путь hero
 * из кадра. Знак задаёт глубину: дальний слой отстаёт от прокрутки (+), ближний
 * обгоняет её (−). Максимум по модулю — 16 px, сильно ниже потолка 24–32 px: на
 * телефоне картинка с большим сдвигом начинает «плыть» относительно текста.
 */
export const LANDING_PARALLAX = {back: 16, front: 6, float: -14} as const;

/**
 * Дыхание фона hero. `period` — полный цикл «вдох-выдох», с; `scale` — на сколько
 * растёт пятно на вдохе; `dim` — минимальная непрозрачность на выдохе (максимум
 * = исходные 1.0, то есть свечение никогда не ярче, чем было, и контраст текста
 * не хуже прежнего). Второе пятно идёт с другим периодом, чтобы цвета не
 * синхронизировались в «пульс».
 */
export const LANDING_GLOW = {period: 9, periodAlt: 13, scale: 1.12, dim: 0.62} as const;

/** Свечение главной CTA: период, с, и минимальная непрозрачность ореола. */
export const LANDING_CTA_GLOW = {period: 3.6, dim: 0.35} as const;

/**
 * Единый эффект кнопок-действий (скачать, создать анкету, войти, выбрать тариф).
 *   lift      — подъём под указателем, px (только hover-устройства);
 *   hover     — время отклика на наведение, мс (короче появления блока, как
 *               LANDING_HOVER_SPRING; в CSS кривая --rl-ease-out, пружину CSS не умеет);
 *   sweep     — пробег блика по малой кнопке при наведении, мс;
 *   shimmer   — период блика на главных CTA, с; `shimmerDelay` — пауза до первого
 *               пробега, `shimmerOffset` — сдвиг второй кнопки в ряду, чтобы блики
 *               шли по очереди, а не вспыхивали разом;
 *   iconDrop  — на сколько px опускается иконка загрузки при наведении.
 */
export const LANDING_CTA = {
    lift: 2,
    hover: 240,
    sweep: 700,
    shimmer: 5.5,
    shimmerDelay: 1.2,
    shimmerOffset: 0.9,
    iconDrop: 2,
} as const;

/** Сжатие кнопки при нажатии и время нажатия/возврата, мс. */
export const LANDING_PRESS = {scale: 0.96, down: 110, up: 260} as const;

/** Блик по карточке при наведении: длительность пробега, мс, и яркость, 0..1. */
export const LANDING_SHINE = {duration: 720, alpha: 0.5} as const;

/** Счётчик процентов: пружина без отскока (значение не перелетает цель). */
export const LANDING_COUNT_SPRING = {type: "spring", bounce: 0, duration: 1.4} as const;

/** Пауза перед стартом счёта, с: даём блоку доехать, чтобы цифры не бежали за кадром. */
export const LANDING_COUNT_DELAY = 0.25;

/**
 * Подсветка шагов: полоса в середине экрана, куда шаг должен попасть, и пауза
 * между соседними шагами, с. Если на широком экране все три шага в полосе
 * одновременно, пауза выстраивает их в очередь.
 */
export const LANDING_LIT = {margin: "-38% 0px -38% 0px", gap: 0.16} as const;

/**
 * Числа из этой таблицы отдаются в CSS переменными на корне лендинга: у
 * keyframes и transition нет другого способа узнать значения из TypeScript.
 */
export const landingCssVars = {
    "--rl-glow-period": `${LANDING_GLOW.period}s`,
    "--rl-glow-period-alt": `${LANDING_GLOW.periodAlt}s`,
    "--rl-glow-scale": String(LANDING_GLOW.scale),
    "--rl-glow-dim": String(LANDING_GLOW.dim),
    "--rl-cta-glow-period": `${LANDING_CTA_GLOW.period}s`,
    "--rl-cta-glow-dim": String(LANDING_CTA_GLOW.dim),
    "--rl-cta-lift": String(LANDING_CTA.lift),
    "--rl-cta-hover": `${LANDING_CTA.hover}ms`,
    "--rl-cta-sweep": `${LANDING_CTA.sweep}ms`,
    "--rl-cta-shimmer-period": `${LANDING_CTA.shimmer}s`,
    "--rl-cta-shimmer-delay": `${LANDING_CTA.shimmerDelay}s`,
    "--rl-cta-shimmer-offset": `${LANDING_CTA.shimmerOffset}s`,
    "--rl-cta-icon-drop": String(LANDING_CTA.iconDrop),
    "--rl-press-scale": String(LANDING_PRESS.scale),
    "--rl-press-down": `${LANDING_PRESS.down}ms`,
    "--rl-press-up": `${LANDING_PRESS.up}ms`,
    "--rl-shine-duration": `${LANDING_SHINE.duration}ms`,
    "--rl-shine-alpha": String(LANDING_SHINE.alpha),
} as CSSProperties;
